import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Pod_sebou (Vertical Arithmetic) - Column addition/subtraction/multiplication
 *
 * Layout: 9 portrait pages.
 * Each page: 5 rows, each with an image + multiple vertical equations.
 * Pages progress through: +20, −20, +100, −100, +1000, −1000, ×2×1, ×3×1, ×3×2.
 *
 * Migrated from C# EduFrame.Img10.Generators.Pod_sebou
 */
export class PodSebou implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const count = 5;
    const size = { width: PdfSize.A4.width, height: PdfSize.A4.height / count };
    const sz1 = { width: size.height - 2 * margin, height: size.height - 2 * margin };

    const list: { fn: () => Eq; width: number }[] = [
      { fn: () => this.plus(opts, 20), width: 80 },
      { fn: () => this.minus(opts, 20), width: 80 },
      { fn: () => this.plus(opts, 100), width: 80 },
      { fn: () => this.minus(opts, 100), width: 80 },
      { fn: () => this.plus(opts, 1000), width: 100 },
      { fn: () => this.minus(opts, 1000), width: 100 },
      { fn: () => this.times(opts, 2, 1), width: 100 },
      { fn: () => this.times(opts, 3, 1), width: 100 },
      { fn: () => this.times(opts, 3, 2), width: 120 },
    ];

    const boldPen = new PdfPen(GUtil.basePen.color, GUtil.basePen.thickness * 3);

    for (let page = 0; page < 9 && page < list.length; page++) {
      const pg = pdf.addPage();
      const item = list[page];
      const cnt = Math.floor((size.width - sz1.width - 3 * margin) / item.width);
      const width = (size.width - sz1.width - 3 * margin) / cnt;
      const labels = Array.from({ length: opts.list.length }, (_, i) => i).sort(
        () => opts.data.random.next(0, 2) - 1
      );

      for (let i = 0; i < count; i++) {
        const rect = new PdfRect(
          0,
          (i * PdfSize.A4.height) / count,
          PdfSize.A4.width,
          PdfSize.A4.height / count
        );
        const rectImg = new PdfRect(rect.x + margin, rect.y + margin, sz1.width, sz1.height);

        const labelIndex = labels[i % labels.length] % opts.list.length;
        pg.drawImageCenter(opts.list[labelIndex].image, rectImg);

        for (let x = 0; x < cnt; x++) {
          const r = new PdfRect(
            rect.x + 2 * margin + sz1.width + x * width,
            rect.y + margin,
            width,
            sz1.height
          );
          const eq = item.fn();
          const lineHeight = r.height / 5;

          const r1 = new PdfRect(r.x, r.y, r.width, lineHeight);
          const r2 = new PdfRect(r.x, r.y + lineHeight, r.width, lineHeight);

          pg.drawString(
            `${eq.top} `,
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            r1,
            ETextAlign.CenterRight
          );
          pg.drawString(
            `${eq.op} ${eq.bottom} `,
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            r2,
            ETextAlign.CenterRight
          );

          pg.drawLine(GUtil.basePen, new PdfPoint(r.right, r.y), new PdfPoint(r.right, r.bottom));
          pg.drawLine(
            boldPen,
            new PdfPoint(r2.x + 5, r2.bottom),
            new PdfPoint(r2.right, r2.bottom)
          );
        }

        pg.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }

  private arrayGen(
    random: any,
    count: number,
    min: number,
    max: number,
    condition?: (arr: number[]) => boolean
  ): number[] {
    while (true) {
      const arr = Array.from({ length: count }, () => min + random.next(0, max - min + 1));
      if (!condition || condition(arr)) return arr;
    }
  }

  private number(random: any, count: number): number {
    const n0 = 1 + random.next(0, 9);
    const digits = [n0];
    for (let i = 0; i < count - 1; i++) {
      digits.push(random.next(0, 10));
    }
    return digits.reduce((c, d) => c * 10 + d, 0);
  }

  private plus(opts: OptsImage, max: number): Eq {
    const arr = this.arrayGen(opts.data.random, 2, 1, max, (n) => n[0] + n[1] < max);
    return { top: `${arr[0]}`, bottom: `${arr[1]}`, op: '+' };
  }

  private minus(opts: OptsImage, max: number): Eq {
    const arr = this.arrayGen(opts.data.random, 2, 1, max, (n) => n[0] + n[1] < max);
    return { top: `${arr[0] + arr[1]}`, bottom: `${arr[1]}`, op: '−' };
  }

  private times(opts: OptsImage, a: number, b: number): Eq {
    return {
      top: `${this.number(opts.data.random, a)}`,
      bottom: `${this.number(opts.data.random, b)}`,
      op: '×',
    };
  }
}

interface Eq {
  top: string;
  bottom: string;
  op: string;
}
